import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';

import { useAuth } from '../contexts/AuthContext';
import {
  ApiError,
  activitiesApi,
  bpmnApi,
  participationApi,
  sessionDurationMinutes,
  MODULE_LABELS,
  MODULE_ORDER,
  type Activity,
  type ActivityInstanceSummary,
  type ModuleState,
  type ModuleType,
  type MyInstance,
  type ParticipationSession,
} from '../services/api/client';

const STATUS_LABEL = {
  DRAFT: 'Rascunho',
  PUBLISHED: 'Publicada',
  ARCHIVED: 'Encerrada',
} as const;

export default function ActivityDetailPage() {
  const { classId = '', activityId = '' } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isProfessor = user?.role === 'PROFESSOR';

  const [activity, setActivity] = useState<Activity | null>(null);
  const [modules, setModules] = useState<ModuleState[]>([]);
  const [error, setError] = useState('');

  const reload = useCallback(async () => {
    try {
      const [list, modulesResult] = await Promise.all([
        activitiesApi.list(classId),
        activitiesApi.modules(activityId),
      ]);

      const found = list.activities.find((item) => item.id === activityId);

      if (!found) {
        setError('Atividade não encontrada.');
        return;
      }

      setActivity(found);
      setModules(modulesResult.modules);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Falha ao carregar.');
    }
  }, [classId, activityId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  if (error) {
    return (
      <>
        <p className="form-error">{error}</p>
        <Link to={`/turmas/${classId}`}>Voltar para a turma</Link>
      </>
    );
  }

  if (!activity) return <p className="muted">Carregando…</p>;

  async function handleStatus(status: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED') {
    try {
      await activitiesApi.update(activityId, { status });
      await reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Falha ao alterar o status.');
    }
  }

  async function handleDelete() {
    if (!window.confirm(`Excluir a atividade "${activity!.title}"?`)) return;

    try {
      await activitiesApi.remove(activityId);
      void navigate(`/turmas/${classId}`, { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Falha ao excluir.');
    }
  }

  return (
    <>
      <div className="section-head">
        <div>
          <h2>{activity.title}</h2>
          <p className="muted">
            {activity.type === 'GROUP' ? 'Em grupo' : 'Individual'}
            {activity.flowType && ` · ${activity.flowType === 'AS_IS' ? 'AS-IS' : 'TO-BE'}`}
            {' · '}
            {STATUS_LABEL[activity.status]}
          </p>
        </div>
        <Link to={`/turmas/${classId}`} className="button-link">
          Voltar
        </Link>
      </div>

      {activity.description && (
        <section className="card">
          <h3>Enunciado</h3>
          <p className="muted preserve-lines">{activity.description}</p>
        </section>
      )}

      {isProfessor ? (
        <>
          <ModulesPanel
            activityId={activityId}
            modules={modules}
            onChange={setModules}
          />
          <InstancesPanel activityId={activityId} activityType={activity.type} />
          <ParticipationPanel activityId={activityId} />

          <section className="card">
            <h3>Situação da atividade</h3>
            <p className="muted">
              Em <strong>rascunho</strong>, os alunos não veem a atividade. Ao{' '}
              <strong>publicar</strong>, os projetos são criados para todos os
              grupos ou alunos da turma.
            </p>
            <div className="button-row">
              {activity.status !== 'PUBLISHED' && (
                <button type="button" className="primary" onClick={() => void handleStatus('PUBLISHED')}>
                  Publicar
                </button>
              )}
              {activity.status === 'PUBLISHED' && (
                <>
                  <button type="button" onClick={() => void handleStatus('DRAFT')}>
                    Voltar para rascunho
                  </button>
                  <button type="button" onClick={() => void handleStatus('ARCHIVED')}>
                    Encerrar
                  </button>
                </>
              )}
              <button type="button" className="link-danger" onClick={() => void handleDelete()}>
                Excluir atividade
              </button>
            </div>
          </section>
        </>
      ) : (
        <StudentActivityView activityId={activityId} modules={modules} />
      )}
    </>
  );
}

/** Liberação de módulos — o coração do controle do professor (PRD seção 9). */
function ModulesPanel({
  activityId,
  modules,
  onChange,
}: {
  activityId: string;
  modules: ModuleState[];
  onChange: (modules: ModuleState[]) => void;
}) {
  const [busy, setBusy] = useState<ModuleType | null>(null);
  const [error, setError] = useState('');

  const byType = new Map(modules.map((module) => [module.moduleType, module]));
  const applicable = MODULE_ORDER.filter((type) => byType.has(type));

  async function toggle(moduleType: ModuleType, enabled: boolean) {
    setBusy(moduleType);
    setError('');

    try {
      const result = await activitiesApi.setModule(activityId, moduleType, enabled);
      onChange(result.modules);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Falha ao alterar o módulo.');
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="card">
      <h3>Módulos liberados</h3>
      <p className="muted">
        Você decide o que os alunos podem acessar, e em que ordem. Nada aqui é
        obrigatório — a sequência abaixo é apenas a metodologia sugerida.
      </p>

      {error && <p className="form-error">{error}</p>}

      <ul className="module-list">
        {applicable.map((moduleType) => {
          const module = byType.get(moduleType)!;

          return (
            <li key={moduleType} className={module.enabled ? 'module-on' : ''}>
              <label className="module-toggle">
                <input
                  type="checkbox"
                  checked={module.enabled}
                  disabled={busy === moduleType}
                  onChange={(e) => void toggle(moduleType, e.target.checked)}
                />
                <span className="module-name">{MODULE_LABELS[moduleType]}</span>
              </label>

              {module.enabled && module.releasedAt && (
                <span className="muted small">
                  liberado em{' '}
                  {new Date(module.releasedAt).toLocaleDateString('pt-BR')}
                  {module.releasedByName && ` por ${module.releasedByName}`}
                </span>
              )}
            </li>
          );
        })}
      </ul>

      <p className="muted small">
        Análise do Processo e Ishikawa aparecem apenas em atividades em grupo.
      </p>
    </section>
  );
}

function InstancesPanel({
  activityId,
  activityType,
}: {
  activityId: string;
  activityType: 'INDIVIDUAL' | 'GROUP';
}) {
  const [instances, setInstances] = useState<ActivityInstanceSummary[]>([]);
  const [error, setError] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [message, setMessage] = useState('');

  const load = useCallback(async () => {
    try {
      const result = await activitiesApi.instances(activityId);
      setInstances(result.instances);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Falha ao carregar.');
    }
  }, [activityId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleSync() {
    setSyncing(true);
    setMessage('');

    try {
      const result = await activitiesApi.syncInstances(activityId);
      setMessage(
        result.created === 0
          ? 'Todos já tinham projeto.'
          : `${result.created} projeto(s) criado(s).`,
      );
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Falha ao sincronizar.');
    } finally {
      setSyncing(false);
    }
  }

  return (
    <section className="card">
      <div className="card-head">
        <h3>Projetos ({instances.length})</h3>
        <div className="button-row">
          <a className="button-link" href={bpmnApi.exportAllUrl(activityId)} download>
            Exportar todos (.zip)
          </a>
          <button type="button" onClick={() => void handleSync()} disabled={syncing}>
            {syncing ? 'Sincronizando…' : 'Sincronizar'}
          </button>
        </div>
      </div>

      <p className="muted">
        Um projeto por {activityType === 'GROUP' ? 'grupo' : 'aluno'}. Quem entrar na
        turma depois ganha o projeto automaticamente ao abrir a atividade.
      </p>

      {error && <p className="form-error">{error}</p>}
      {message && <p className="muted small">{message}</p>}

      {instances.length === 0 ? (
        <p className="muted">
          Nenhum projeto ainda. Publique a atividade ou use Sincronizar.
        </p>
      ) : (
        <ul className="simple-list">
          {instances.map((instance) => (
            <li key={instance.id}>
              <Link to={`/projetos/${instance.id}/bpmn`}>{instance.ownerName}</Link>
              <span className="muted small">
                {instance.ownerType === 'GROUP'
                  ? `${instance.memberCount} integrante(s)`
                  : 'individual'}
                {' · '}
                <Link to={`/projetos/${instance.id}/visao-futura`}>visão futura</Link>
                {' · '}
                <Link to={`/projetos/${instance.id}/sipoc`}>sipoc</Link>
                {' · '}
                <Link to={`/projetos/${instance.id}/analise`}>análise</Link>
                {' · '}
                <Link to={`/projetos/${instance.id}/problemas`}>problemas</Link>
                {' · '}
                <Link to={`/projetos/${instance.id}/okr`}>okr</Link>
                {' · '}
                <Link to={`/projetos/${instance.id}/plano-implantacao`}>plano</Link>
                {' · '}
                <Link to={`/projetos/${instance.id}/gantt`}>gantt</Link>
                {' · '}
                <Link to={`/projetos/${instance.id}/pdca`}>pdca</Link>
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function StudentActivityView({
  activityId,
  modules,
}: {
  activityId: string;
  modules: ModuleState[];
}) {
  const [instance, setInstance] = useState<MyInstance | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    activitiesApi
      .myInstance(activityId)
      .then(setInstance)
      .catch((err: unknown) =>
        setError(err instanceof ApiError ? err.message : 'Falha ao abrir o projeto.'),
      );
  }, [activityId]);

  const released = modules.filter((module) => module.enabled);

  return (
    <section className="card">
      <h3>Seu projeto</h3>

      {error && <p className="form-error">{error}</p>}

      {instance && (
        <p className="muted">
          {instance.ownerType === 'GROUP'
            ? `Projeto do ${instance.groupName}.`
            : 'Projeto individual.'}
        </p>
      )}

      <h3 className="spaced">Etapas disponíveis</h3>

      {released.length === 0 ? (
        <p className="muted">
          Nenhuma etapa liberada ainda. O professor libera os módulos conforme a
          disciplina avança.
        </p>
      ) : (
        <ul className="module-list">
          {MODULE_ORDER.filter((type) =>
            released.some((module) => module.moduleType === type),
          ).map((type) => {
            const path =
              type === 'FUTURE_VISION'
                ? 'visao-futura'
                : type === 'SIPOC'
                  ? 'sipoc'
                  : type === 'BPMN' || type === 'TO_BE'
                    ? 'bpmn'
                    : type === 'PROCESS_ANALYSIS'
                      ? 'analise'
                      : type === 'PROBLEM' || type === 'ISHIKAWA'
                        ? // Ishikawa não é 1 por projeto, e sim 1 por problema
                          // (PRD 15.2) — o aluno escolhe o problema na tela de
                          // Problemas e abre o Ishikawa daquele problema a
                          // partir de lá.
                          'problemas'
                        : type === 'OKR'
                          ? 'okr'
                          : type === 'IMPLEMENTATION_PLAN'
                            ? 'plano-implantacao'
                            : type === 'GANTT'
                              ? 'gantt'
                              : type === 'PDCA'
                                ? 'pdca'
                                : null;

            const openable = path && instance;

            return (
              <li key={type} className="module-on">
                <span className="module-name">{MODULE_LABELS[type]}</span>
                <div className="button-row">
                  {openable ? (
                    <Link
                      to={`/projetos/${instance.instanceId}/${path}`}
                      className="button-link"
                    >
                      Abrir
                    </Link>
                  ) : (
                    <span className="muted small">disponível em breve</span>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

/**
 * Participação (FASE 8) — visão do professor.
 *
 * Lista crua das sessões de entrada/saída. Deliberadamente sem agregação,
 * filtro ou gráfico — isso é a FASE 16 (dashboard). Aqui é só a prova de que
 * o registro está acontecendo direito.
 */
function ParticipationPanel({ activityId }: { activityId: string }) {
  const [sessions, setSessions] = useState<ParticipationSession[] | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    participationApi
      .list(activityId)
      .then((result) => setSessions(result.sessions))
      .catch((err: unknown) =>
        setError(err instanceof ApiError ? err.message : 'Falha ao carregar participação.'),
      );
  }, [activityId]);

  return (
    <section className="card">
      <h3>Participação</h3>
      <p className="muted">
        Sessões de entrada e saída dos alunos nesta atividade. Não conta
        interações — só o tempo em que cada um esteve com o módulo aberto.
      </p>

      {error && <p className="form-error">{error}</p>}
      {!sessions && !error && <p className="muted">Carregando…</p>}

      {sessions?.length === 0 && (
        <p className="muted">Nenhuma sessão registrada ainda.</p>
      )}

      {sessions && sessions.length > 0 && (
        <table className="data-table">
          <thead>
            <tr>
              <th>Aluno</th>
              <th>Módulo</th>
              <th>Entrada</th>
              <th>Saída</th>
              <th>Duração</th>
            </tr>
          </thead>
          <tbody>
            {sessions.map((session) => {
              const minutes = sessionDurationMinutes(session);

              return (
                <tr key={session.id}>
                  <td>{session.studentName}</td>
                  <td className="muted">{MODULE_LABELS[session.moduleType]}</td>
                  <td className="muted small">
                    {new Date(session.startedAt).toLocaleString('pt-BR', {
                      dateStyle: 'short',
                      timeStyle: 'short',
                    })}
                  </td>
                  <td className="muted small">
                    {session.ongoing ? (
                      <span className="badge badge-ok">em andamento</span>
                    ) : (
                      new Date(session.endedAt!).toLocaleString('pt-BR', {
                        dateStyle: 'short',
                        timeStyle: 'short',
                      })
                    )}
                  </td>
                  <td className="muted">{minutes !== null ? `${minutes} min` : '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}
